import { useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../lib/axios';
import type {
  Order,
  OrderCreatePayload,
  OrderPaymentStatus,
  OrderFulfillmentStatus,
  OrderDeliveryAddress,
  PaymentMethod,
} from '../types';

// ============================================
// Payload Types for Order Mutations
// ============================================

/**
 * Payload for updating an order's general details
 */
export interface UpdateOrderPayload {
  // Customer information
  customerEmail?: string;
  customerPhone?: string;
  customerName?: string;
  // Delivery information
  deliveryAddress?: OrderDeliveryAddress;
  pickupAddress?: string;
  // Financial fields
  tax?: number;
  discount?: number;
  // Payment information
  paymentMethod?: PaymentMethod;
  // Reservation/scheduling fields
  expiresAt?: string;
  requiresPickup?: boolean;
  pickupScheduledAt?: string;
  // Order notes
  notes?: string;
  // Notification and completion fields
  notificationSent?: boolean;
  pickedUpAt?: string;
  receiptNumber?: string;
  completedAt?: string;
  // Refund fields
  isRefunded?: boolean;
  refundedAmount?: number;
  refundedAt?: string;
  // Update comment for history
  updateComment?: string;
}

/**
 * Payload for updating payment status
 */
export interface UpdatePaymentStatusPayload {
  status: OrderPaymentStatus;
  metadata?: Record<string, unknown>;
  notes?: string;
}

/**
 * Payload for updating fulfillment status
 */
export interface UpdateFulfillmentStatusPayload {
  status: OrderFulfillmentStatus;
  metadata?: Record<string, unknown>;
  notes?: string;
}

/**
 * Payload for cancelling an order
 */
export interface CancelOrderPayload {
  reason?: string;
}

// ============================================
// API Functions for Order Mutations
// ============================================

export const orderMutationsApi = {
  /**
   * Create a new order
   */
  createOrder: async (payload: OrderCreatePayload): Promise<Order> => {
    const response = await axiosInstance.post<Order>('/orders', payload);
    return response.data;
  },

  /**
   * Update order general details
   */
  updateOrder: async (id: string, payload: UpdateOrderPayload): Promise<Order> => {
    const response = await axiosInstance.patch<Order>(`/orders/${id}`, payload);
    return response.data;
  },

  /**
   * Update order payment status
   */
  updatePaymentStatus: async (id: string, payload: UpdatePaymentStatusPayload): Promise<Order> => {
    const response = await axiosInstance.patch<Order>(`/orders/${id}/payment-status`, payload);
    return response.data;
  },

  /**
   * Update order fulfillment status
   */
  updateFulfillmentStatus: async (
    id: string,
    payload: UpdateFulfillmentStatusPayload
  ): Promise<Order> => {
    const response = await axiosInstance.patch<Order>(`/orders/${id}/fulfillment-status`, payload);
    return response.data;
  },

  /**
   * Cancel an order
   */
  cancelOrder: async (id: string, reason?: string): Promise<Order> => {
    const response = await axiosInstance.post<Order>(`/orders/${id}/cancel`, { reason });
    return response.data;
  },

  /**
   * Delete an order (soft delete)
   */
  deleteOrder: async (id: string): Promise<void> => {
    await axiosInstance.delete(`/orders/${id}`);
  },
};

// ============================================
// Mutation Hooks
// ============================================

/**
 * Hook for creating a new order.
 *
 * @returns TanStack Mutation for order creation
 *
 * @example
 * ```tsx
 * const { mutate: createOrder, isPending } = useCreateOrder();
 *
 * const handleCreateOrder = () => {
 *   createOrder({
 *     orderType: OrderType.B2C,
 *     locationId: 'location-uuid',
 *     items: [{ productId: 'product-uuid', quantity: 2 }],
 *     fulfillmentMethod: FulfillmentMethod.PICKUP,
 *     paymentMethod: 'cash',
 *   }, {
 *     onSuccess: (order) => console.log('Order created:', order.trackingNumber),
 *   });
 * };
 * ```
 */
export const useCreateOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: OrderCreatePayload) => orderMutationsApi.createOrder(payload),
    onSuccess: () => {
      // Invalidate orders list to show the new order
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });
};

/**
 * Hook for updating order general details.
 *
 * @returns TanStack Mutation for order update
 *
 * @example
 * ```tsx
 * const { mutate: updateOrder } = useUpdateOrder();
 *
 * updateOrder({
 *   id: 'order-uuid',
 *   payload: { notes: 'Updated delivery instructions' },
 * });
 * ```
 */
export const useUpdateOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateOrderPayload }) =>
      orderMutationsApi.updateOrder(id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['order', variables.id] });
    },
  });
};

/**
 * Hook for updating order payment status.
 *
 * @returns TanStack Mutation for payment status update
 *
 * @example
 * ```tsx
 * const { mutate: updatePaymentStatus } = useUpdatePaymentStatus();
 *
 * updatePaymentStatus({
 *   id: 'order-uuid',
 *   payload: {
 *     status: OrderPaymentStatus.PAID,
 *     notes: 'Payment received via cash',
 *   },
 * });
 * ```
 */
export const useUpdatePaymentStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdatePaymentStatusPayload }) =>
      orderMutationsApi.updatePaymentStatus(id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['order', variables.id] });
    },
  });
};

/**
 * Hook for updating order fulfillment status.
 *
 * @returns TanStack Mutation for fulfillment status update
 *
 * @example
 * ```tsx
 * const { mutate: updateFulfillmentStatus } = useUpdateFulfillmentStatus();
 *
 * updateFulfillmentStatus({
 *   id: 'order-uuid',
 *   payload: {
 *     status: OrderFulfillmentStatus.READY_FOR_PICKUP,
 *     notes: 'Order ready at counter 1',
 *   },
 * });
 * ```
 */
export const useUpdateFulfillmentStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateFulfillmentStatusPayload }) =>
      orderMutationsApi.updateFulfillmentStatus(id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['order', variables.id] });
    },
  });
};

/**
 * Hook for cancelling an order.
 *
 * @returns TanStack Mutation for order cancellation
 *
 * @example
 * ```tsx
 * const { mutate: cancelOrder } = useCancelOrder();
 *
 * cancelOrder({
 *   id: 'order-uuid',
 *   reason: 'Customer requested cancellation',
 * });
 * ```
 */
export const useCancelOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      orderMutationsApi.cancelOrder(id, reason),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['order', variables.id] });
    },
  });
};

/**
 * Hook for deleting an order (soft delete).
 *
 * @returns TanStack Mutation for order deletion
 *
 * @example
 * ```tsx
 * const { mutate: deleteOrder } = useDeleteOrder();
 *
 * deleteOrder('order-uuid', {
 *   onSuccess: () => navigate('/orders'),
 * });
 * ```
 */
export const useDeleteOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => orderMutationsApi.deleteOrder(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });
};

// ============================================
// Combined Hook for All Order Mutations
// ============================================

/**
 * Combined hook that provides all order mutation operations.
 *
 * This hook is useful when you need access to multiple order mutations
 * in the same component.
 *
 * @returns Object containing all order mutation hooks
 *
 * @example
 * ```tsx
 * const {
 *   createOrder,
 *   updateOrder,
 *   updatePaymentStatus,
 *   updateFulfillmentStatus,
 *   cancelOrder,
 *   deleteOrder,
 * } = useOrderMutations();
 *
 * // Use individual mutations as needed
 * createOrder.mutate({ ... });
 * updatePaymentStatus.mutate({ id: '...', payload: { status: OrderPaymentStatus.PAID } });
 * ```
 */
export const useOrderMutations = () => {
  return {
    createOrder: useCreateOrder(),
    updateOrder: useUpdateOrder(),
    updatePaymentStatus: useUpdatePaymentStatus(),
    updateFulfillmentStatus: useUpdateFulfillmentStatus(),
    cancelOrder: useCancelOrder(),
    deleteOrder: useDeleteOrder(),
  };
};

// Re-export types for convenience
export type { OrderCreatePayload } from '../types';
